import { z } from "zod";

import { refuseDisagreeingMoneyPeriodBasis } from "../../../../shared/domain/role-coverage/coverage-money-basis-agreement";
import { RoleSystemImpactInputSchema } from "../../../../shared/domain/role-coverage/role-coverage-authoring-input.schema";
import {
  GrantedRoleSystemsSchema,
  RoleResourceLifecycleSchema
} from "../../../../shared/domain/role-coverage/role-coverage-view.schema";
import {
  RoleResourceTypeSchema,
  RoleRuntimeCallerResourceTypeSchema
} from "../../../../shared/domain/roles/role.types";

/**
 * Core Roles schemas — the Role row itself, the resource references it owns,
 * the path variables every route spells, and the CRUD request/response shapes.
 */

/**
 * A resource id as a CALLER supplies it — on a request, never on a response.
 *
 * All five tables a `RoleResourceType` can name (`Agent`, `Workflow`,
 * `Deployment`, `DocumentTemplate`, `AITask`) key on
 * `@db.Uuid`, so a malformed id has no valid answer: Postgres cannot compare it
 * to a uuid column at all. Left as a bare `z.string()` it reaches Prisma and
 * raises `P2023`, which `PrismaClientExceptionFilter` maps to
 * `400 "Malformed identifier in the request path"` — so the STATUS is already
 * right without this schema, and it is the rest that this check buys: the
 * request is refused before it touches the database, and the response names the
 * offending FIELD instead of serving one flat sentence for the whole request.
 *
 * Declared ONCE and shared by every request that carries one, so a fifth
 * request site cannot be added with a bare `z.string()` and quietly fall back to
 * that generic answer. `id-validation-consistency.test.ts` enforces the same thing from
 * the other direction: once `resourceId` is uuid-checked on one request in this
 * manifest, leaving another unchecked fails that suite by name.
 *
 * This does NOT tighten what a valid client could ever send — every id the UI
 * has to offer is a uuid it read back from this same API.
 */
export const RoleResourceIdSchema = z.string().uuid();

/**
 * One resource that belongs to a Role, as the API RETURNS it.
 *
 * Deliberately NOT uuid-checked, and this is the one place the distinction
 * matters. `RoleResource.resourceId` is a loose TEXT column with no foreign
 * key, and rows written before the existence guard existed can hold a value
 * that is not a uuid. Tightening a RESPONSE turns one such legacy row into a
 * failed listing for the whole Role — a read that breaks on data already in the
 * database, rather than a write that refuses bad input. Requests get
 * `RoleResourceIdSchema`; reads stay permissive on purpose.
 */
export const RoleResourceRefSchema = z.object({
  resourceType: RoleResourceTypeSchema,
  resourceId: z.string()
});

export type RoleResourceRef = z.infer<typeof RoleResourceRefSchema>;

/** The same pair on the way IN — where a malformed id is the caller's error to fix. */
export const RoleResourceRefInputSchema = z.object({
  resourceType: RoleResourceTypeSchema,
  resourceId: RoleResourceIdSchema
});

/**
 * A Role as the API returns it. Dates are ISO strings — the controller
 * serializes the domain entity's `Date` fields.
 *
 * `ownerUserId` is nullable because the column is (ON DELETE SET NULL, so
 * deleting a user never wedges their Roles), even though the create path always
 * supplies one.
 */
export const RoleSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  jobDescription: z.string().nullable(),
  ownerUserId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * When this Role's work was stopped. `null` — the overwhelming majority —
   * means it is running.
   *
   * A TIMESTAMP RATHER THAN A BOOLEAN, and the extra fact is the reason: every
   * surface that draws a paused state has to say *since when*, and a boolean
   * cannot answer that at any price. `null` as the running state is also the
   * safe default, so a Role that predates this column reads as running rather
   * than as stopped.
   *
   * 🚨 IT IS ABOUT EXECUTION AND SAYS NOTHING ABOUT ACCESS. Pausing a Role does
   * not suspend, narrow or revoke anything it grants — see `RoleCapability`'s
   * `role.pause` for why the access reading was refused rather than deferred.
   * A member of a paused Role reaches exactly what they reached before.
   *
   * ⚠️ AND IT DOES NOT STOP EVERYTHING THE ROLE HOLDS, WHICH IS A PROPERTY OF
   * THE PLATFORM RATHER THAN OF THIS FIELD. Of the six kinds a Role can hold,
   * only `workflow` and `agent` have an execution guard to be held by. A
   * `deployment` has `isActive` and no guard tier; an `ai_task` has no off
   * switch at all; a `document_template` is `DRAFT | SAVED`, which is not
   * on/off; and an `external_tool` is a status on the SHARED catalogue row, so
   * flipping it would reach every other tenant holding that tool. **Any copy
   * rendered over this field must name what actually stops** — a bare "Paused"
   * chip over a Role whose deployments keep serving is precisely the control
   * `UnbackedNote` forbids.
   */
  pausedAt: z.string().nullable(),
  /**
   * Who stopped it. `null` while the Role is running, and also `null` for a
   * paused Role whose pauser has since been deleted — the column is
   * `ON DELETE SET NULL`, mirroring `ownerUserId`, so removing a user never
   * wedges a Role in a state nobody can name.
   *
   * 🔴 SO `pausedAt !== null && pausedByUserId === null` IS A REAL AND
   * REACHABLE STATE, and it is NOT "not paused". Read the stop off `pausedAt`
   * alone; this field decides only whether a byline can be drawn.
   */
  pausedByUserId: z.string().nullable()
});

export type Role = z.infer<typeof RoleSchema>;

/**
 * The single-Role read — the only shape that carries the membership list.
 *
 * ## `resources` is ONE of the FOUR tables a Role holds systems through
 *
 * 🚨 A READER OF THIS SHAPE ALONE CANNOT TELL "this Role holds nothing" FROM
 * "this Role holds nothing OF THE KIND THIS FIELD CARRIES", AND ONE SCREEN
 * ASSERTED THE FIRST FOR A YEAR. `resources` is `RoleResource` — the
 * exclusively-owned membership. `RoleCollectionGrant`, `RoleWorkspaceGrant` and
 * `RoleExternalToolGrant` are M:N grants that exist because a Collection, a
 * Workspace and an external Tool can each be shared across Roles, and none of
 * them can appear here: `RoleResourceRefSchema.resourceType` is a
 * `RoleResourceType`, which those three kinds deliberately left.
 *
 * Meanwhile `OrgRoleRow.holdings` on the Roles index counts all four, and the
 * Overview board draws all four. Measured on production 2026-08-14,
 * `Finance Operations`: index **17**, board **17 cards**, this read **16**.
 *
 * ⚠️ THAT FIELD IS `holdings`, NOT `systems`, AND THE DIFFERENCE IS THIS
 * MODULE'S VOCABULARY RATHER THAN A RENAME —
 * `org-roles-rollup-view.schema.ts` reserves "system" for the OWNABLE kinds and
 * contrasts them with grants. So `grantedSystems` here is named for the
 * population it counts on the COVERAGE view it is borrowed from; do not read
 * it as a claim that a grant is a "system" in the index's sense.
 *
 * `grantedSystems` is what closes that, and it is COUNTS rather than
 * identities — {@link GrantedRoleSystemsSchema} carries the reasoning, and the
 * three grant kinds each have their own route and their own tab where they are
 * listed properly. A surface rendering `resources` is describing part of what
 * the Role holds; this field is what lets it say which part.
 *
 * ⚠️ IT IS REQUIRED, NOT OPTIONAL, AND THAT IS THE POINT. An optional field
 * lets a producer omit it and a consumer read the omission as a zero, which is
 * the same wrong sentence in a new costume.
 */
export const RoleWithResourcesSchema = RoleSchema.extend({
  resources: z.array(RoleResourceRefSchema),
  grantedSystems: GrantedRoleSystemsSchema,
  /**
   * THE ROLE'S CUE — which agent answers for this Role.
   *
   * `null` means this Role designates no Cue, which is every row the day the
   * column appears and is a resting state rather than a gap to backfill.
   *
   * 🚨 IT IS THE RESOLVED DESIGNATION, NOT THE STORED COLUMN, AND THE TWO CAN
   * DIFFER. `Role.cueAgentId`'s foreign key carries `ON DELETE SET NULL`, so a
   * HARD-deleted agent clears itself. `Agent.deletedAt` is a SOFT delete and the
   * foreign key is blind to every one of them — 122 of 1,377 production agents
   * are in that state — so the column can go on naming an agent no screen can
   * open. It is `null` in that case; in the (write-path-refused, but
   * database-representable) case where the column names another organisation's
   * agent; and where the agent has since LEFT the Role, which is the ordinary
   * one — detaching moves nothing else, and attaching an agent to another Role
   * MOVES it, because `RoleResource` is exclusive.
   *
   * All four mean the same thing: there is no Cue to draw. The alternative is a
   * card that renders blank against a 404, or a Role showing an agent it does not
   * hold. `FindLiveCueAgentIdPort` is the one place that narrowing happens, and
   * it asks exactly the questions `UpdateRoleUseCase` asked at the write.
   *
   * ⚠️ SO A `null` HERE DOES NOT MEAN THE COLUMN IS `null`, and no surface may
   * read it as "nothing was ever designated". It means there is no Cue to draw.
   *
   * REQUIRED, never optional, for the same reason `grantedSystems` above is: an
   * optional field lets a producer omit it and a consumer read the omission as
   * "no Cue", which is a measurement nobody took.
   *
   * On the DETAIL shape rather than on `RoleSchema`, and that is deliberate. The
   * narrowing above needs a join against `Agent`, and no surface reading the
   * Roles LIST draws a Cue — putting it on `RoleSchema` would buy that join on
   * every row of every org-wide list read to publish a field nothing renders.
   * Publishing the raw column there instead is the one thing that must not
   * happen: it is the un-narrowed value this field exists to replace.
   */
  cueAgentId: z.string().nullable()
});

export type RoleWithResources = z.infer<typeof RoleWithResourcesSchema>;

export const RolePathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * One row of the organisation's job-type library, named on the path.
 *
 * UUID-CHECKED, as every Role path id is since NEX-3552 tightened them together.
 * `jobTypeId` also arrives inside `RoleScopeLineInputSchema`, where it is
 * `z.string().uuid()`, so leaving it loose here would be the partial tightening
 * `id-validation-consistency.test.ts` refuses. `RoleJobType.id` is `@db.Uuid`,
 * so a malformed value cannot be compared to the column at all: left loose it
 * reaches Prisma and raises `P2023`, which `PrismaClientExceptionFilter` maps to
 * a flat `400 "Malformed identifier in the request path"` — the right status,
 * but one sentence for the whole request rather than the field that is wrong.
 * Checking here refuses before the query runs.
 */
export const RoleJobTypePathVarsSchema = z.object({
  jobTypeId: z.string().uuid()
});

export const RoleResourcePathVarsSchema = RoleResourceRefInputSchema;

/**
 * Path vars for `GET .../retrieval-confinement` — an Agent or Workflow id,
 * never the other three `RoleResourceType` members. See
 * `RoleRuntimeCallerResourceTypeSchema` for why: a Deployment, an AI task and
 * a Document template never execute, so no agent-runtime caller identity can
 * ever be resolved for one, and this route has no answer to give.
 */
export const RoleRuntimeCallerResourcePathVarsSchema = z.object({
  resourceType: RoleRuntimeCallerResourceTypeSchema,
  resourceId: RoleResourceIdSchema
});

/**
 * The two-value UI-facing reduction of the five-kind runtime confinement
 * verdict, for one Agent or Workflow. See
 * `apps/backend/src/roles/domain/value-objects/zero-access-confinement.ts`
 * for what each value means and why it is not a boolean.
 */
export const RoleZeroAccessConfinementSchema = z.enum(["unconfined", "orphaned_zero_access"]);

export const RoleResourceRetrievalConfinementResponseSchema = z.object({
  retrievalConfinement: RoleZeroAccessConfinementSchema
});

/**
 * The Role and the `RoleResource` row an impact model belongs to.
 *
 * BOTH ARE UUID-CHECKED. Both columns are `String @id @db.Uuid`, so a malformed
 * value cannot be compared to either at all.
 *
 * 🚨 THE TWO SEGMENTS STILL DO NOT ANSWER THE SAME WAY, despite reading
 * identically here. `id` is shape-checked at RUNTIME by `RoleIdShapeGuard`,
 * mounted on this controller, and a guard runs BEFORE the handler parses these
 * path vars — so a malformed `:id` is answered 404 and never reaches this schema
 * at all. That is the point rather than an accident: 404 is the same answer an
 * absent Role and another organization's Role get, so malformed, absent and
 * foreign stay indistinguishable, where the 400 a Zod refusal serves is a
 * discriminator a stranger could read. Nothing guards `roleResourceId`, so THAT
 * segment is refused here, with a 400.
 *
 * The uuid checks were only ever available as a
 * MANIFEST-WIDE change (NEX-3552). `id-validation-consistency.test.ts` enforces
 * the invariant from two directions at once — an object may not uuid-check one
 * id and leave a sibling unchecked, AND once an id NAME is uuid-checked
 * anywhere in a manifest every request object carrying that name must check it
 * too — so tightening this one pair alone was refused, and the only spelling
 * the gate accepts is every Role path id together.
 *
 * Two fields are deliberately NOT uuid-checked and carry an allowlist entry
 * each: `RoleMemberPathVarsSchema.userId` is a Clerk id and never a uuid, and
 * `RoleBoardCardPathVarsSchema.cardId` is polymorphic — `cardType`
 * discriminates eight kinds, three of which reach a card through a GRANT table
 * rather than `RoleResource`.
 */
export const RoleSystemImpactPathVarsSchema = z.object({
  id: z.string().uuid(),
  roleResourceId: z.string().uuid()
});

/**
 * The bucket a caller is asking a system to move into.
 *
 * Reuses the READ contract's own schema rather than re-declaring the three
 * values: a write vocabulary that could drift from the vocabulary the coverage
 * response publishes is a fourth state waiting to be invented, and the
 * lifecycle is one column with one set of legal values.
 *
 * WHICH MOVES ARE LEGAL IS NOT EXPRESSED HERE, and cannot be. The legal edges
 * depend on the row's CURRENT state, which a request body has never seen —
 * `role-resource-lifecycle-transition.ts` is the state machine and it is
 * server-side by necessity. This schema's whole job is to refuse a value that
 * is not a lifecycle at all.
 */
export const RoleSystemLifecycleBodySchema = z.object({
  lifecycle: RoleResourceLifecycleSchema
});

/**
 * What the transition did.
 *
 * It echoes the state rather than answering `{ success: true }`, because a
 * caller that retried after a timeout needs to know WHICH state it is looking
 * at, and the alternative is a second round trip to find out. It is not the
 * whole `RoleResource`: nothing else about the row changed, and returning a
 * fuller shape would invite a client to re-render from a payload that carries
 * none of the coverage figures the move just altered.
 */
export const RoleSystemLifecycleResponseSchema = z.object({
  roleResourceId: z.string(),
  lifecycle: RoleResourceLifecycleSchema
});

export const RolesListResponseSchema = z.object({
  roles: z.array(RoleSchema)
});

export type RolesListResponse = z.infer<typeof RolesListResponseSchema>;

export const RoleCreateBodySchema = z.object({
  name: z.string().trim().min(1, "Role name is required").max(255),
  jobDescription: z.string().max(10_000).nullable().optional(),
  /**
   * Optional on the wire: the controller defaults it to the calling user, so a
   * Role is never blocked on picking an owner yet never owner-less either.
   */
  ownerUserId: z.string().optional()
});

export const RoleUpdateBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  jobDescription: z.string().max(10_000).nullable().optional(),
  ownerUserId: z.string().nullable().optional(),
  /**
   * Designate the Role's Cue — the agent that answers for it.
   *
   * The same tri-state patch contract `ownerUserId` carries: absent leaves the
   * designation alone, `null` clears it, an id designates that agent.
   *
   * UUID-CHECKED, unlike its `ownerUserId` sibling, and the mix is the point
   * rather than an inconsistency. `Agent.id` is `String @id @db.Uuid`, so a
   * malformed value cannot be compared to the column at all and only ever
   * reaches Postgres as a failed cast; `User.id` is a bare `String @id` holding
   * Clerk ids (`user_…`), so uuid-checking THAT would refuse live traffic.
   * `id-validation-consistency.test.ts` refuses a silent mix, so
   * `ZRoles.Update.Body` carries an entry in its `MIXED_SIBLINGS_ALLOWED` list
   * naming exactly this reason.
   *
   * 🚨 THE SCHEMA PROVES THE SHAPE AND NOTHING ELSE. Whether the id names an
   * agent that EXISTS, is LIVE, belongs to the CALLER'S organisation, and is one
   * of THIS Role's own agents are four separate questions, and the database can
   * answer only the first: the foreign key is single-column, so a cross-tenant
   * `cueAgentId` is accepted by Postgres. `UpdateRoleUseCase` is the only thing
   * that refuses one — see its `verifiedCueAgent`.
   */
  cueAgentId: z.string().uuid().nullable().optional()
});

export const RoleAssignResourceBodySchema = RoleResourceRefInputSchema;

/** Unassign is idempotent — `removed` says whether a membership actually went. */
export const RoleUnassignResourceResponseSchema = z.object({
  removed: z.boolean()
});

/**
 * `synced` counts resources the backfill actually touched (see BackfillRbacEnforcement
 * below) — not every RoleResource in the org, since one already-synced or
 * individually-failing resource does not stop the rest.
 */
export const RoleBackfillRbacEnforcementResponseSchema = z.object({
  synced: z.number()
});

/**
 * The two path segments a graduation names: the Role, and the proposal inside it.
 *
 * BOTH ARE UUID-CHECKED, AND THAT IS THE MANIFEST'S DECISION RATHER THAN THIS
 * ROUTE'S — the identical ruling `RoleSystemImpactPathVarsSchema` carries one
 * screen above. `id-validation-consistency.test.ts` holds a field NAME to one
 * verdict across the whole `ZRoles` manifest, so uuid-checking `taskId` while a
 * sibling `id` stayed loose is the PARTIAL tightening that suite refuses; the
 * only spelling it accepts is every Role path id together (NEX-3552). Two
 * fields stay bare and carry an allowlist entry each —
 * `RoleMemberPathVarsSchema.userId` is a Clerk id that is genuinely never a
 * uuid, and `RoleBoardCardPathVarsSchema.cardId` is polymorphic.
 *
 * ⚠️ `taskId` IS SHAPE-CHECKED DOWNSTREAM AS WELL, AND NOT BY THE GUARD.
 * `RoleIdShapeGuard` reads `params.id` and nothing else, by design, so it
 * answers a malformed Role id with 404 and is silent on a malformed task id.
 * `RoleTask.id` is `@db.Uuid`, so a value that reached Prisma unchecked would
 * raise `P2023`, which `PrismaClientExceptionFilter` maps to a flat 400.
 * `GraduateRoleTaskUseCase` performs its own syntactic check instead and
 * answers the same 404 an absent task gets — malformed, absent and foreign stay
 * one indistinguishable answer, which is the tenancy property `role.errors.ts`
 * defends. The controller parses these path vars before that use case runs, so
 * the use case's check is the floor for any caller not arriving through the
 * manifest.
 */
export const RoleTaskGraduationPathVarsSchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid()
});

/**
 * Turning a PROPOSED task into a system the Role really holds and really prices.
 *
 * ONE BODY, TWO CLAIMS, AND THEY ARE COMPOSED RATHER THAN RESTATED. The
 * resource half is `RoleResourceRefInputSchema` — the same pair
 * `AssignResource` takes, so `resourceId` inherits `RoleResourceIdSchema` and
 * cannot drift from it. The pricing half is `RoleSystemImpactInputSchema` — the
 * same three formulas `UpsertRoleSystemImpact` takes. Spreading both `.shape`s
 * is what makes a future change to either one reach this route with no edit
 * here; restating the five fields would be a third spelling to keep in step.
 *
 * ⚠️ THE TWO MONEY MODELS ARE THE ONE DELIBERATE DIVERGENCE, AND THEY ARE
 * DERIVED FROM THAT SAME SHAPE RATHER THAN RESTATED — see the field comments
 * below. A graduation may omit them, an impact write may not, because only one
 * of the two routes is authoring a price.
 *
 * 🚨 IT NAMES A SYSTEM THAT ALREADY EXISTS. Graduation does not BUILD anything —
 * `RoleResource.resourceId` must name a live row in one of five real tables, so
 * the caller has to have created the agent or workflow first. That is the whole
 * asymmetry with the reference design's `Approve to build` button, which flips a
 * field on a local object and has no system to point at.
 *
 * STRICT, so a caller that sends a stray `name` or `occurrencesPerYear` — the
 * task's own authored figures, which are tempting to echo back — is refused
 * rather than silently ignored. Those figures are the CLIENT's input to the
 * formulas it sends; the server never re-derives them.
 *
 * 🚨 THE SPREAD COPIES FIELDS AND LEAVES REFINEMENTS BEHIND, so
 * `refuseDisagreeingMoneyPeriodBasis` is re-applied here EXPLICITLY. It is the
 * same callback the impact body uses, not a restatement of the rule — but
 * `...Schema.shape` is a shape, so nothing carries it across on its own and a
 * body composed this way would inherit the two money models without the rule
 * that keeps their windows in step. `coverage-money-basis-agreement.test.ts`
 * derives every schema holding both fields and fails one that does not refuse,
 * so this line cannot be dropped quietly.
 */
export const RoleTaskGraduationInputSchema = z
  .strictObject({
    ...RoleResourceRefInputSchema.shape,
    ...RoleSystemImpactInputSchema.shape,
    /**
     * 🚨 OPTIONAL HERE, REQUIRED-AND-NULLABLE ON THE IMPACT BODY, AND THE
     * DIFFERENCE IS A DATA-LOSS FIX RATHER THAN A RELAXATION (NEX-3823).
     *
     * This route ATTACHES and then PRICES, and the attach is an upsert whose
     * `RoleResource` id survives a move — so `RoleSystemImpact.roleResourceId`,
     * which is `@unique`, routinely names a row that is already priced. On the
     * impact body a caller is authoring the two money models and `null` is a
     * removal; here a caller is authoring HOURS, and `GRADUATION_FORM_COPY`
     * declares no money section at all, so the dialog has nothing to say about
     * price. With the fields required there was no way to SAY nothing, the
     * dialog said `null`, and every graduation onto an already-priced system
     * deleted that system's cost and revenue models.
     *
     * So: ABSENT preserves, `null` clears, a formula sets. Removing a price
     * while graduating stays expressible — it is just no longer what silence
     * means. `optionalMoneyWrite` on the server is the one place that reading
     * is applied.
     *
     * ⚠️ DERIVED FROM THE IMPACT SHAPE, NEVER RESTATED. `CoverageFormulaSchema`
     * spelled again here would be a third copy of the tree grammar and would
     * drift the first time the impact body narrows it; `.optional()` returns a
     * NEW schema and leaves the impact body's own field untouched.
     */
    revenueFormula: RoleSystemImpactInputSchema.shape.revenueFormula.optional(),
    costFormula: RoleSystemImpactInputSchema.shape.costFormula.optional()
  })
  .superRefine(refuseDisagreeingMoneyPeriodBasis);

export type RoleTaskGraduationInput = z.infer<typeof RoleTaskGraduationInputSchema>;
